import {
  Component,
  Input,
  OnInit,
  OnChanges,
  EventEmitter,
  Output
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

interface PageLink {
  text: string;
  enable: boolean;
  active: boolean;
  queryParams: any;
  page: number;
}

@Component({
  selector: 'ct-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.css']
})
export class PaginationComponent implements OnInit, OnChanges {
  @Input()
  smallSize = false;
  /** limit number for page links in pager */
  @Input()
  maxSize = 0;

  @Input()
  baseLink: any[];

  @Input()
  pageSize = 10;

  @Input()
  totalItems: number;

  @Input()
  pageIndex = 1;

  @Input()
  disable = false;

  @Input()
  boundaryLinks = true;

  @Input()
  directionLinks = true;

  @Input()
  displayEllipsesWhenNotShowingAllPageNumbers = true;

  @Input()
  useEvent = false;

  @Input()
  queryParams: any;

  pages: PageLink[];

  @Output()
  pageChanged = new EventEmitter<PageLink>();

  constructor(private router: ActivatedRoute) { }

  ngOnInit(): void {
    this.router.url.subscribe(url => {
      this.baseLink = this.baseLink || url;
    });

    this.pages = this.getPages(this.pageIndex, this.calculateTotalPages());
  }

  ngOnChanges(): void {
    this.router.url.subscribe(url => {
      this.baseLink = this.baseLink || url;
    });

    this.pages = this.getPages(this.pageIndex, this.calculateTotalPages());
  }

  private createPage(
    pageNo: number,
    text: string,
    active: boolean,
    enable: boolean
  ): PageLink {
    return <PageLink>{
      active: active,
      enable: enable && !this.disable,
      text: text,
      queryParams: {
        ...this.queryParams,
        ...{ pageIndex: pageNo, pageSize: this.pageSize }
      },
      page: pageNo
    };
  }

  private computePageToDisplay(
    currentPage: number,
    totalPages: number
  ): { start: number; end: number } {
    if (totalPages > this.maxSize) {
      let firstPage = Math.max(Math.floor(currentPage - this.maxSize / 2), 1);

      const lastPage = Math.min(firstPage + this.maxSize - 1, totalPages);
      if (lastPage >= totalPages) {
        firstPage = Math.max(totalPages - this.maxSize + 1, 1);
      }

      return {
        start: firstPage,
        end: lastPage
      };
    } else {
      return {
        start: 1,
        end: totalPages
      };
    }
  }
  private getPages(currentPage: number, totalPages: number): PageLink[] {
    const pages: PageLink[] = [];

    // first
    if (this.boundaryLinks) {
      const firstPage = this.createPage(1, 'First', false, this.pageIndex > 1);
      pages.push(firstPage);
    }

    // previous
    if (this.directionLinks) {
      const prevPage = this.createPage(
        this.pageIndex - 1,
        'Previous',
        false,
        this.pageIndex > 1
      );
      pages.push(prevPage);
    }

    // add page number links
    const { start, end } = this.computePageToDisplay(currentPage, totalPages);

    // ellipses
    if (this.displayEllipsesWhenNotShowingAllPageNumbers && start > 1) {
      const ellipsesPage = this.createPage(this.pageIndex, '...', false, false);
      pages.push(ellipsesPage);
    }
    for (let num = start; num <= end; num++) {
      const page = this.createPage(
        num,
        num.toString(),
        num === currentPage,
        true
      );
      pages.push(page);
    }

    // ellipses
    if (this.displayEllipsesWhenNotShowingAllPageNumbers && end < totalPages) {
      const ellipsesPage = this.createPage(this.pageIndex, '...', false, false);
      pages.push(ellipsesPage);
    }
    // next
    if (this.directionLinks) {
      const nextPage = this.createPage(
        this.pageIndex + 1,
        'Next',
        false,
        this.pageIndex < totalPages
      );
      pages.push(nextPage);
    }

    // last
    if (this.boundaryLinks) {
      const lastPage = this.createPage(
        totalPages,
        'Last',
        false,
        this.pageIndex < totalPages
      );
      pages.push(lastPage);
    }

    return pages;
  }

  private calculateTotalPages(): number {
    const totalPages =
      this.pageSize < 1 ? 1 : Math.ceil(this.totalItems / this.pageSize);

    return Math.max(totalPages || 0, 1);
  }

  /* method for ngx paging compatibility */
  public selectPage(page: PageLink, event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (!this.disable) {
      this.pageChanged.emit(page);
    }
  }
}
